import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BrandFieldKey, BrandImportJobView, BrandOnboardingStartMode, BrandSuggestion } from '@mg/shared';
import { api } from '../../../lib/api';
import { useIsoLayoutEffect } from '../../../lib/useIsoLayoutEffect';
import { SUGGESTIONS_ENABLED } from '../../brand/flags/suggestions';
import { brandKeys, IMPORT_POLL_CEILING_MS, isImportActive } from '../../brand/queries';
import { useBrandDraft } from '../draft/BrandDraftContext';
import { isEmptyValue } from '../draft/brandDraft';
import { copySuggestedValue, keysToAsk, offeredSuggestions, suggestionRequest } from './suggestionRules';

/**
 * AI suggestions for Brand Setup's empty fields (spec:113-125; web.md §5.8; final-design §9, lane W6),
 * around the whole wizard. On while features/brand/flags/suggestions.ts is; off, it passes its children
 * through and no field is offered anything.
 *
 * - **What is asked:** the empty, untouched fields of the step on the page (each field's SuggestionSlot
 *   registers it), once the step has stayed up for SUGGESTION_DELAY_MS, so skipping through steps asks
 *   nothing. The request carries what the wizard holds now, saved or not, so what the customer entered in
 *   earlier steps feeds later suggestions (spec:119); the server adds the sources, then the name and
 *   website (spec:120-121). Each field is asked once per visit; a failed ask is asked again when its step
 *   is shown again.
 * - **While an import runs** (Upload and prefill), nothing is asked: a suggestion is for what the sources
 *   left empty (spec:117), never for a field the import is about to fill.
 * - **Never filled in by itself, never saved** (spec:115, :122): an answer is held as an offer, drawn under
 *   its field while the field is empty. Use suggestion fills the field as an unsaved change tagged "AI
 *   suggested" (`setFields`, web.md §5.8); the step save stores it. Dismiss takes the offer and its tag
 *   away for the rest of the visit (spec:124).
 * - A new start choice or a new import (another document) asks again: the basis changed.
 *
 * Nothing re-renders outside a render, a click or an answer to offer: a timer firing or a request failing
 * only updates bookkeeping, and a request still out when the wizard closes is cancelled.
 */

/** How long a step's empty fields stay on the page before they are asked for. */
export const SUGGESTION_DELAY_MS = 700;

/** The suggestions on offer, by field. */
export type SuggestionOffers = Partial<Record<BrandFieldKey, BrandSuggestion>>;

export interface SuggestionsContextValue {
  /** Each field's offer (shown only while the field is empty: useFieldSuggestion). */
  offers: SuggestionOffers;
  /** Fields whose suggestion the customer dismissed: none is offered again in this visit. */
  dismissed: ReadonlySet<BrandFieldKey>;
  /** A field is on the page (its suggestion slot is mounted): it may be asked for. Returns its removal. */
  register: (key: BrandFieldKey) => () => void;
  /** Use suggestion: an empty field takes its offer, tagged "AI suggested", as an unsaved change. */
  use: (key: BrandFieldKey) => void;
  /** Dismiss: the offer, and with it the field's "AI suggested" tag, go. */
  dismiss: (key: BrandFieldKey) => void;
}

const SuggestionsContext = createContext<SuggestionsContextValue | null>(null);

/** The wizard's suggestions; null where there are none (the flag file is off, or outside the wizard). */
export function useSuggestions(): SuggestionsContextValue | null {
  return useContext(SuggestionsContext);
}

export function SuggestionsProvider({
  children,
  delayMs = SUGGESTION_DELAY_MS,
}: {
  children: ReactNode;
  /** SUGGESTION_DELAY_MS; a spec may shorten it. */
  delayMs?: number;
}) {
  if (!SUGGESTIONS_ENABLED) return <>{children}</>;
  return <OfferSuggestions delayMs={delayMs}>{children}</OfferSuggestions>;
}

type AskState = 'pending' | 'answered' | 'failed';

function OfferSuggestions({ children, delayMs }: { children: ReactNode; delayMs: number }) {
  const { state, dispatch } = useBrandDraft();
  const tenantId = state.tenantId;
  const importBusy = useImportBusy(tenantId, state.startMode);
  const [offers, setOffers] = useState<SuggestionOffers>({});
  const [dismissed, setDismissed] = useState<ReadonlySet<BrandFieldKey>>(() => new Set());
  // Bumped when a field comes or goes, so the asking below runs with the step's fields in place.
  const [, setShownTick] = useState(0);

  // Bookkeeping that draws nothing.
  const shown = useRef(new Map<BrandFieldKey, number>());
  const asked = useRef(new Map<BrandFieldKey, AskState>());
  const generation = useRef(0);
  const inFlight = useRef(new Set<AbortController>());
  const timer = useRef<{ id: ReturnType<typeof setTimeout>; keys: string } | null>(null);

  // What a timer or a click reads when it runs: the draft as it is then (an import's fill may have landed).
  const live = useRef({ state, importBusy, offers, dismissed });
  useIsoLayoutEffect(() => {
    live.current = { state, importBusy, offers, dismissed };
  });

  /** The fields to ask for now: none while an import may still fill them. */
  const wanted = useCallback((): BrandFieldKey[] => {
    const now = live.current;
    if (now.importBusy) return [];
    return keysToAsk(now.state, shown.current.keys(), (key) => asked.current.has(key) || now.dismissed.has(key));
  }, []);

  const ask = useCallback(() => {
    const keys = wanted();
    if (keys.length === 0) return;
    const round = generation.current;
    for (const key of keys) asked.current.set(key, 'pending');
    const controller = new AbortController();
    inFlight.current.add(controller);
    api
      .getBrandSuggestions(tenantId, suggestionRequest(live.current.state, keys), controller.signal)
      .then((response) => {
        if (round !== generation.current) return;
        for (const key of keys) asked.current.set(key, 'answered');
        const answers = offeredSuggestions(response, keys);
        if (answers.length === 0) return;
        setOffers((prev) => {
          const next = { ...prev };
          for (const answer of answers) next[answer.key] = answer;
          return next;
        });
      })
      .catch(() => {
        // Nothing is drawn for a failure (a suggestion is an extra); its fields are asked again when
        // their step is shown again (register).
        if (round !== generation.current) return;
        for (const key of keys) asked.current.set(key, 'failed');
      })
      .finally(() => {
        inFlight.current.delete(controller);
      });
  }, [tenantId, wanted]);

  // A new start choice, or a new import merged (another document): the basis changed, so every field is
  // asked again and what is on offer goes. Answers still out are dropped. Dismissals stand.
  const basis = `${state.startMode}|${state.mergedJobId ?? ''}`;
  const lastBasis = useRef(basis);
  useEffect(() => {
    if (lastBasis.current === basis) return;
    lastBasis.current = basis;
    generation.current += 1;
    asked.current.clear();
    setOffers((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [basis]);

  // After every render: (re)arm the one timer for the fields to ask for now, or disarm it.
  useEffect(() => {
    const keys = wanted();
    const signature = keys.join(',');
    if (timer.current?.keys === signature) return;
    if (timer.current) clearTimeout(timer.current.id);
    timer.current = null;
    if (keys.length === 0) return;
    timer.current = {
      keys: signature,
      id: setTimeout(() => {
        timer.current = null;
        ask();
      }, delayMs),
    };
  });

  // Closing the wizard: no timer fires and no answer lands. (React's development double mount runs this
  // too: an ask it cancelled is asked again.)
  useEffect(() => {
    const requests = inFlight.current;
    const statuses = asked.current;
    return () => {
      if (timer.current) clearTimeout(timer.current.id);
      timer.current = null;
      generation.current += 1;
      for (const controller of requests) controller.abort();
      requests.clear();
      for (const [key, status] of statuses) if (status === 'pending') statuses.delete(key);
    };
  }, []);

  const register = useCallback((key: BrandFieldKey) => {
    const onPage = shown.current;
    onPage.set(key, (onPage.get(key) ?? 0) + 1);
    // Shown again after a failed ask: ask again.
    if (asked.current.get(key) === 'failed') asked.current.delete(key);
    setShownTick((n) => n + 1);
    return () => {
      const left = (onPage.get(key) ?? 1) - 1;
      if (left > 0) onPage.set(key, left);
      else onPage.delete(key);
      setShownTick((n) => n + 1);
    };
  }, []);

  const use = useCallback(
    (key: BrandFieldKey) => {
      const now = live.current;
      const offer = now.offers[key];
      // Only ever into an empty field: Use suggestion never replaces content (spec:117).
      if (!offer || now.dismissed.has(key) || !isEmptyValue(now.state.fields[key].value)) return;
      dispatch({
        type: 'setFields',
        // The customer's change, tagged "AI suggested"; an empty field had no source tags (spec:109, :125).
        changes: [{ key, value: copySuggestedValue(offer.value), sources: [], aiSuggested: true, touched: true }],
      });
    },
    [dispatch],
  );

  const dismiss = useCallback((key: BrandFieldKey) => {
    setDismissed((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
    setOffers((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo<SuggestionsContextValue>(
    () => ({ offers, dismissed, register, use, dismiss }),
    [offers, dismissed, register, use, dismiss],
  );
  return <SuggestionsContext.Provider value={value}>{children}</SuggestionsContext.Provider>;
}

/** A running import past the wizard's ceiling no longer holds suggestions back (its banner offers Try again). */
function stillRunning(job: BrandImportJobView | null): boolean {
  if (!job || !isImportActive(job)) return false;
  const started = Date.parse(job.runStartedAt);
  return Number.isNaN(started) || Date.now() - started < IMPORT_POLL_CEILING_MS;
}

/**
 * Whether Upload and prefill's import may still fill fields: the workspace's latest import is being read
 * (it just started or ended), or its job is still running. It reads the queries the wizard's import
 * already follows (useImportJob) and never fetches them itself. Fill in manually waits for nothing.
 */
function useImportBusy(tenantId: string, startMode: BrandOnboardingStartMode): boolean {
  const latest = useQuery({
    queryKey: brandKeys.latestImport(tenantId),
    queryFn: ({ signal }) => api.getLatestBrandImport(tenantId, signal),
    enabled: false,
  });
  const latestJob = latest.data?.job?.purpose === 'onboarding' ? latest.data.job : null;
  const jobId = latestJob?.id ?? '';
  const followed = useQuery({
    queryKey: brandKeys.importJob(tenantId, jobId),
    queryFn: ({ signal }) => api.getBrandImportJob(tenantId, jobId, signal),
    enabled: false,
  });
  if (startMode !== 'UPLOAD') return false;
  if (latest.isFetching) return true;
  return stillRunning(followed.data ?? latestJob);
}
